import axios, { AxiosError, AxiosResponse } from 'axios';
import { Client, createClient } from 'graphql-ws';

import { LoginPayload, LoginResponse } from 'types/auth';
import { User } from 'types/user';
import { getRedirectTo } from 'utils/getRedirectTo';

const lastSavedForm = new Map<number, Record<string, any>>();
export class Api {
  constructor(
    public readonly client = axios.create({ baseURL: '/api/v1' }),
    public readonly hasuraClient = axios.create({
      baseURL: '/v1/graphql',
    }),
    private hasuraWsClient: Client | null = null,
    private readonly hasuraMutationClient = axios.create({
      baseURL: '/api/v1/hasura/mutation',
    }),
  ) {
    const responseInterceptor = (response: AxiosResponse<any, any>) => {
      if (response.data?.errors?.length) {
        if (
          response.data?.errors[0]?.message?.includes(`not found in type: '`) &&
          !window.location.pathname.includes('/login')
        ) {
          window.location.href = getRedirectTo();
        }
      }
      return response;
    };

    const errorInterceptor = (error: AxiosError) => {
      if (
        !window.location.pathname.includes('/login') &&
        (error.response?.status === 403 || error.response?.status === 401)
      ) {
        window.location.href = getRedirectTo();
      }
      return Promise.reject(error);
    };

    this.client.interceptors.response.use(
      responseInterceptor,
      errorInterceptor,
    );

    this.hasuraClient.interceptors.response
      .use
      // responseInterceptor,
      // errorInterceptor,
      ();

    this.hasuraMutationClient.interceptors.response.use(
      responseInterceptor,
      errorInterceptor,
    );
  }

  get instance() {
    return this.client;
  }

  get hasuraClientInstance() {
    return this.hasuraClient;
  }

  get hasuraWsClientInstance() {
    return this.hasuraWsClient;
  }

  get hasuraMutationClientInstance() {
    return this.hasuraMutationClient;
  }

  initializeWsConnection() {
    this.hasuraWsClient = createClient({
      url:
        process.env.NODE_ENV === 'development'
          ? 'ws://localhost:8082/v1/graphql'
          : `wss://${window.location.host}/v1/graphql`,
      shouldRetry: () => true,
    });
  }

  async getMe() {
    try {
      const { data } = await this.client.get<User>('/auth');
      return data;
    } catch {
      return undefined;
    }
  }

  async getSettings() {
    const {
      data: {
        data: { directories },
      },
    } = await this.hasuraClientInstance.post<{
      data: {
        directories: any[];
      };
    }>('', {
      query: `
        {
          directories: directory {
              id
              name
              formattedName
              emailAccountSuffix
          }
        }`
        .replace(/\n/g, ' ')
        .replace(/ +/g, ' ')
        .trim(),
    });

    return { directories };
  }

  async getForm(slug: string, clientId: number) {
    const {
      data: {
        data: { webForm },
      },
    } = await this.hasuraClientInstance.post<{
      data: {
        webForm: [
          {
            id: number;
            name: string;
            slug: string;
            data: Record<string, any>;
            clients: {
              data: Record<string, any>;
              isFilled: boolean;
              filledAt: string | null;
            }[];
          },
        ];
      };
    }>('', {
      query: `
        query getForm($slug: String!, $clientId: Int!) {
          webForm(where: { slug: { _eq: $slug } }) {
            id
            name
            slug
            clients(where: { clientId: { _eq: $clientId } }) {
              isFilled
              filledAt
              data
            }
          }
        }`
        .replace(/\n/g, ' ')
        .replace(/ +/g, ' ')
        .trim(),
      variables: {
        slug,
        clientId,
      },
    });

    if (!webForm.length) {
      return null;
    }

    const {
      clients: [{ data, isFilled, filledAt }],
      ...form
    } = webForm[0];

    lastSavedForm.set(form.id, data ?? {});

    return { ...form, data: data ?? {}, isFilled, filledAt };
  }

  async saveForm(id: number, data: Record<string, any>) {
    if (
      lastSavedForm.has(id) &&
      JSON.stringify(lastSavedForm.get(id)) === JSON.stringify(data)
    ) {
      return;
    }

    await this.hasuraClientInstance.post('', {
      query: `
        mutation saveForm($id: Int!, $data: jsonb!) {
          updateClientWebForm(where: { webFormId: { _eq: $id } }, _set: { data: $data }) {
            affected_rows
          }
        }`
        .replace(/\n/g, ' ')
        .replace(/ +/g, ' ')
        .trim(),
      variables: {
        id,
        data,
      },
    });

    lastSavedForm.set(id, data);
  }

  async fillForm(id: number) {
    await this.hasuraClientInstance.post('', {
      query: `
        mutation fillForm($id: Int!, $set: ClientWebFormSetInput) {
          updateClientWebForm(where: { webFormId: { _eq: $id } }, _set: $set) {
            affected_rows
          }
        }`
        .replace(/\n/g, ' ')
        .replace(/ +/g, ' ')
        .trim(),
      variables: {
        id,
        set: {
          isFilled: true,
          filledAt: new Date().toISOString(),
        },
      },
    });
  }

  async login(payload: LoginPayload): Promise<LoginResponse> {
    const { data } = await this.client.post('/auth/login', payload);

    return data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }
}

export const api = new Api();
